import React, { useState } from 'react';
import { FormField } from './FormField';
import { DollarSignIcon, InfoIcon, LinkIcon, FormInputIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, CalendarIcon, ExternalLinkIcon, PlusIcon, ShareIcon, SettingsIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { CaptureField, CaptureFieldsEditor } from './CaptureFieldsEditor';
import { ShareLinkModal } from './ShareLinkModal';
interface StaticLink {
  id: string;
  name: string;
  reference: string;
  amount: string;
  currency: string;
  url: string;
  active: boolean;
  createdAt: string;
  usageCount: number;
  totalCollected: number;
  expiryDate?: string;
}
interface FixedLinksPaymentProps {
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function FixedLinksPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: FixedLinksPaymentProps) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('THB');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [captureFields, setCaptureFields] = useState<CaptureField[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentLinkToShare, setCurrentLinkToShare] = useState<StaticLink | null>(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('23:59');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [existingLinks, setExistingLinks] = useState<StaticLink[]>([{
    id: '1',
    name: 'Monthly Subscription',
    reference: 'monthly-sub',
    amount: '19.99',
    currency: 'THB',
    url: 'https://payment.example.com/pay/monthly-sub',
    active: true,
    createdAt: '2023-05-15',
    usageCount: 47,
    totalCollected: 939.53,
    expiryDate: '2023-12-31 23:59'
  }, {
    id: '2',
    name: 'Premium Plan',
    reference: 'premium-plan',
    amount: '49.99',
    currency: 'THB',
    url: 'https://payment.example.com/pay/premium-plan',
    active: true,
    createdAt: '2023-06-22',
    usageCount: 23,
    totalCollected: 1149.77
  }, {
    id: '3',
    name: 'One-time Donation',
    reference: 'donation-form',
    amount: '10.00',
    currency: 'THB',
    url: 'https://payment.example.com/pay/donation-form',
    active: false,
    createdAt: '2023-08-10',
    usageCount: 156,
    totalCollected: 1560.0,
    expiryDate: '2023-10-15 18:00'
  }]);
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const toggleLinkStatus = (id: string) => {
    setExistingLinks(existingLinks.map(link => link.id === id ? {
      ...link,
      active: !link.active
    } : link));
  };
  const deleteLink = (id: string) => {
    if (confirm('Are you sure you want to permanently delete this payment link?')) {
      setExistingLinks(existingLinks.filter(link => link.id !== id));
    }
  };
  const copyLinkUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      console.log('Link copied to clipboard');
    });
  };
  const handleShareLink = (link: StaticLink) => {
    setCurrentLinkToShare(link);
    setShowShareModal(true);
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = expiryDate ? parseDate(expiryDate) : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() - 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() + 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    if (date) {
      setExpiryDate(formatDate(date));
      setShowDatePicker(false);
    }
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handleCreateLink = () => {
    let expiryDateTimeString = undefined;
    if (hasExpiry && expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => part.trim());
      expiryDateTimeString = `${year}-${month}-${day} ${expiryTime}`;
    }
    const newLink: StaticLink = {
      id: `link-${Date.now()}`,
      name: description || 'New Payment Link',
      reference: reference || `link-${Date.now()}`,
      amount: amount || '0.00',
      currency: currency,
      url: `https://payment.example.com/pay/${reference || Date.now()}`,
      active: true,
      createdAt: new Date().toISOString().split('T')[0],
      usageCount: 0,
      totalCollected: 0,
      expiryDate: expiryDateTimeString
    };
    setExistingLinks([newLink, ...existingLinks]);
    setAmount('');
    setReference('');
    setDescription('');
    setCaptureFields([]);
    setHasExpiry(false);
    setExpiryDate('');
    setExpiryTime('23:59');
  };
  const formatExpiryForDisplay = (expiryString?: string) => {
    if (!expiryString) return 'No expiry';
    const [datePart, timePart] = expiryString.split(' ');
    const date = new Date(datePart + 'T' + timePart);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };
  const isLinkExpired = (expiryString?: string) => {
    if (!expiryString) return false;
    const now = new Date();
    const [datePart, timePart] = expiryString.split(' ');
    const expiryDate = new Date(datePart + 'T' + timePart);
    return now > expiryDate;
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  return <div className="space-y-5" data-id="element-2729">
      {/* ─── EXISTING LINKS ─── */}
      {existingLinks.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2730">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-2731">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-2732">
              <LinkIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-2733" />
              Your fixed payment links
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-2734">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2735">
              <thead className="bg-gray-50" data-id="element-2736">
                <tr data-id="element-2737">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2738">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2739">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2740">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2741">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2742">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2743">
                    Total Collected
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2744">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2745">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2746">
                {existingLinks.map(link => <tr key={link.id} className={!link.active || isLinkExpired(link.expiryDate) ? 'bg-gray-50' : ''} data-id="element-2747">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2748">
                      <div className="text-sm font-medium text-gray-900" data-id="element-2749">
                        {link.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-2750">
                        {link.reference}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2751">
                      THB {link.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2752">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-2753">
                        <CalendarIcon size={14} className="mr-1.5 text-gray-400" data-id="element-2754" />
                        {link.createdAt}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2755">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-2756">
                        <ClockIcon size={14} className="mr-1.5 text-gray-400" data-id="element-2757" />
                        {formatExpiryForDisplay(link.expiryDate)}
                        {isLinkExpired(link.expiryDate) && <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded font-medium" data-id="element-2758">
                            Expired
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2759">
                      {link.usageCount} payments
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2760">
                      THB {link.totalCollected.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2761">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${link.active && !isLinkExpired(link.expiryDate) ? 'bg-[#F7941D]/10 text-[#F7941D]' : 'bg-gray-100 text-gray-800'}`} data-id="element-2762">
                        {link.active && !isLinkExpired(link.expiryDate) ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2763">
                      <div className="flex items-center justify-end space-x-2" data-id="element-2764">
                        <button onClick={() => copyLinkUrl(link.url)} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title="Copy link" data-id="element-2765">
                          <ExternalLinkIcon size={15} data-id="element-2766" />
                        </button>
                        <button onClick={() => handleShareLink(link)} className="p-1.5 rounded-md bg-[#F7941D]/10 text-[#F7941D] hover:bg-[#F7941D]/20 transition-colors" title="Share link" data-id="element-2767">
                          <ShareIcon size={15} data-id="element-2768" />
                        </button>
                        <button onClick={() => toggleLinkStatus(link.id)} className={`p-1.5 rounded-md transition-colors ${link.active ? 'bg-[#F7941D]/10 text-[#F7941D] hover:bg-[#F7941D]/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title={link.active ? 'Disable link' : 'Enable link'} data-id="element-2769">
                          {link.active ? <ToggleRightIcon size={15} data-id="element-2770" /> : <ToggleLeftIcon size={15} data-id="element-2771" />}
                        </button>
                        <button onClick={() => deleteLink(link.id)} className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title="Delete link permanently" data-id="element-2772">
                          <TrashIcon size={15} data-id="element-2773" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {existingLinks.length === 0 && <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-2774">
          <p className="text-gray-500 font-medium" data-id="element-2775">
            You haven't created any fixed payment links yet
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-2776">
            Fill out the form below to create your first fixed payment link
          </p>
        </div>}

      {/* Card 1: Payment details */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2777">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-2778">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-2779">
            <LinkIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-2780" />
            Payment details
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-2781">
          <div data-id="element-2782">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2783">
              Payment amount <span className="text-red-400" data-id="element-2784">*</span>
            </label>
            <div className="relative" data-id="element-2785">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2786">
                <span className="text-sm font-medium text-gray-500" data-id="element-2787">THB</span>
              </div>
              <input type="text" className={`${inputClass} pl-12 pr-20`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-2788" />
              <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-2789">
                <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-lg focus:ring-[#F7941D]/20 focus:border-[#F7941D]" value={currency} onChange={e => handleCurrencyChange(e.target.value)} data-id="element-2790">
                  <option data-id="element-2791">THB</option>
                  <option data-id="element-2792">USD</option>
                  <option data-id="element-2793">EUR</option>
                  <option data-id="element-2794">GBP</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2795">
            <div data-id="element-2796">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2797">
                Link reference
              </label>
              <input type="text" className={inputClass} placeholder="e.g., monthly-subscription or donation-form" value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-2798" />
            </div>
            <div data-id="element-2799">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2800">
                Payment description
              </label>
              <input type="text" className={inputClass} placeholder="e.g., Monthly subscription or Donation" value={description} onChange={e => setDescription(e.target.value)} data-id="element-2801" />
            </div>
          </div>

          {/* Expiry toggle */}
          <div data-id="element-2802">
            <div className="flex items-center" data-id="element-2803">
              <label className="inline-flex items-center cursor-pointer" data-id="element-2804">
                <input type="checkbox" className="sr-only" checked={hasExpiry} onChange={() => setHasExpiry(!hasExpiry)} data-id="element-2805" />
                <div className={`w-10 h-5 rounded-full transition-colors ${hasExpiry ? 'bg-[#F7941D]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-2806">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${hasExpiry ? 'translate-x-5' : ''}`} data-id="element-2807" />
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-2808">
                  Set expiration date and time
                </span>
              </label>
              <InfoTooltip content="Fixed links don't expire by default. Enable this to set an optional expiration date and time." data-id="element-2809" />
            </div>

            {hasExpiry && <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2810">
                <div className="relative" data-id="element-2811">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2812">
                    Expiry date
                  </label>
                  <div className="relative" data-id="element-2813">
                    <input type="text" className={`${inputClass} pl-10`} placeholder="MM/DD/YYYY" value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-2814" />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2815">
                      <CalendarIcon size={16} className="text-gray-400" data-id="element-2816" />
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-2817">
                      {showDatePicker ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-2818" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2819" />}
                    </div>
                  </div>

                  {showDatePicker && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-full" data-id="element-2820">
                      <div className="flex justify-between items-center mb-2" data-id="element-2821">
                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-2822">
                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-2823" />
                        </button>
                        <div className="font-medium text-sm" data-id="element-2824">
                          {calendar.month.toLocaleString('default', {
                      month: 'long',
                      year: 'numeric'
                    })}
                        </div>
                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-2825">
                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-2826" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-2827">
                        <div className="text-gray-500" data-id="element-2828">Su</div>
                        <div className="text-gray-500" data-id="element-2829">Mo</div>
                        <div className="text-gray-500" data-id="element-2830">Tu</div>
                        <div className="text-gray-500" data-id="element-2831">We</div>
                        <div className="text-gray-500" data-id="element-2832">Th</div>
                        <div className="text-gray-500" data-id="element-2833">Fr</div>
                        <div className="text-gray-500" data-id="element-2834">Sa</div>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-2835">
                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                  ${isSelected(date) ? 'bg-[#F7941D] text-white' : ''}
                                  ${isToday(date) && !isSelected(date) ? 'border border-[#F7941D] text-[#F7941D]' : ''}
                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                `} onClick={() => handleDateSelect(date)} data-id="element-2836">
                                {date.getDate()}
                              </button> : <div key={index} className="w-8 h-8" data-id="element-2837"></div>))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-2838">
                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#E8851A]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 7);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-2839">
                          7 days
                        </button>
                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#E8851A]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 30);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-2840">
                          30 days
                        </button>
                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#E8851A]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 90);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-2841">
                          90 days
                        </button>
                      </div>
                    </div>}
                </div>

                <div data-id="element-2842">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2843">
                    Expiry time
                  </label>
                  <div className="relative" data-id="element-2844">
                    <input type="time" className={`${inputClass} pl-10`} value={expiryTime} onChange={e => setExpiryTime(e.target.value)} data-id="element-2845" />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2846">
                      <ClockIcon size={16} className="text-gray-400" data-id="element-2847" />
                    </div>
                  </div>
                  <div className="mt-1.5 text-xs text-gray-500" data-id="element-2848">
                    Time is in your local timezone (
                    {Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Card 2: Customer information */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2849">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-2850">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-2851">
            <FormInputIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-2852" />
            Customer information
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-2853">
            Specify what information to collect from customers before payment.
          </p>
        </div>
        <div className="p-6" data-id="element-2854">
          <CaptureFieldsEditor fields={captureFields} onChange={setCaptureFields} data-id="element-2855" />
        </div>
      </div>

      {/* Card 3: Additional options */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2856">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-2857">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-2858">
            <SettingsIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-2859" />
            Additional options
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-2860">
          <div data-id="element-2861">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2862">
              Success redirect URL
            </label>
            <input type="text" className={inputClass} placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} data-id="element-2863" />
          </div>

          <div data-id="element-2864">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-2865">
              Custom metadata
            </label>
            <button type="button" onClick={() => setMetadata([...metadata, {
            key: '',
            value: ''
          }])} className="inline-flex items-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7941D]/20 transition-colors" data-id="element-2866">
              <PlusIcon size={16} className="mr-1.5 text-gray-400" data-id="element-2867" />
              Add metadata field
            </button>

            {metadata.length > 0 && <div className="mt-3 space-y-3" data-id="element-2868">
                {metadata.map((item, index) => <div key={index} className="flex space-x-2" data-id="element-2869">
                    <input type="text" className={inputClass} placeholder="Key" value={item.key} onChange={e => {
                const updatedMetadata = [...metadata];
                updatedMetadata[index].key = e.target.value;
                setMetadata(updatedMetadata);
              }} data-id="element-2870" />
                    <input type="text" className={inputClass} placeholder="Value" value={item.value} onChange={e => {
                const updatedMetadata = [...metadata];
                updatedMetadata[index].value = e.target.value;
                setMetadata(updatedMetadata);
              }} data-id="element-2871" />
                    <button type="button" onClick={() => {
                const updatedMetadata = [...metadata];
                updatedMetadata.splice(index, 1);
                setMetadata(updatedMetadata);
              }} className="p-2.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50" data-id="element-2872">
                      <TrashIcon size={16} data-id="element-2873" />
                    </button>
                  </div>)}
              </div>}
          </div>
        </div>
      </div>

      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} recipients={[]} data-id="element-2874" />}
    </div>;
}
// Helper component for the info tooltip
function InfoTooltip({
  content
}: {
  content: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative ml-1.5" data-id="element-2875">
      <div className="text-gray-400 hover:text-gray-500 cursor-help" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-2876">
        <InfoIcon size={14} data-id="element-2877" />
      </div>
      {showTooltip && <div className="absolute z-10 w-64 px-3 py-2 text-sm text-left font-normal text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg -top-2 left-6" data-id="element-2878">
          {content}
        </div>}
    </div>;
}